import React, { useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { Collapse } from 'bootstrap';

import Icon from '../components/icon';
import { scrollToHash, setClipboard } from '../utils/misc';

// temporal
const EXAMPLES = [
	{
		key: 'order-limit',
		title: 'Limit order',
		conditions: {type: 'limit'},
		description: 'Set the "limit_price" parameter to create a limit order' +
			' (or "stop_price" for a stop order). There are some optional parameters,' +
			' such as "duration" (also known as time in force),' +
			' that can be useful for limit orders.',
		body: {
			symbol: 'AAPL',
			action: 'buy',
			quantity: 1,
			limit_price: 100,
			duration: 'gtd',
		},
	},
	{
		// reserved / conditions: {limit_price: true},
		key: 'order-bracket',
		title: 'Bracket order',
		conditions: {stop_loss_price: true},
		description: 'Set the "take_profit_price" parameter and/or' +
			' the "stop_loss_price" parameter to create a bracket order with take profit and/or' +
			' stop loss conditions. To create a separate take profit or stop loss order,' +
			' you can use a simple limit or stop order (see the "Limit order" example).',
		body: {
			symbol: 'AAPL',
			action: 'buy',
			quantity: 1,
			take_profit_price: 999,
			stop_loss_price: 99,
		},
	},
	{
		// reserved / conditions: {limit_price: true},
		key: 'order-multileg-combo-iron-condor',
		title: 'Iron condor multileg spread order',
		conditions: {group: 'combo', class: 'option'},
		description: 'Specify legs using indexed parameters,' +
			' to create a multi-legged orders. An indexed parameter consists of' +
			' the regular parameter name followed by an index suffix, for example:' +
			' [2], [3], [4]. The first leg\'s parameters may also be accompanied by' +
			' the suffix [1] or have no suffix at all. The Iron Condor options strategy' +
			' involves creating four-legged orders.',
		body: {
			symbol: 'TSLA_230714P210',
			action: 'buy_to_open',
			quantity: 1,
			'symbol[2]': 'TSLA_230714P215',
			'action[2]': 'sell_to_open',
			'quantity[2]': 1,
			'symbol[3]': 'TSLA_230714C282',
			'action[3]': 'sell_to_open',
			'quantity[3]': 1,
			'symbol[4]': 'TSLA_230714C290',
			'action[4]': 'buy_to_open',
			'quantity[4]': 1,
			limit_price: 123,
		},
	},
	// "bear call spread"
	// {
	//     "time-in-force": "Day",
	//     "price": 0.85,
	//     "price-effect": "Credit",
	//     "order-type": "Limit",
	//     "legs": [
	//       {
	//           "instrument-type": "Equity Option",
	//           "symbol": "AAPL  221118C00155000",
	//           "quantity": 1,
	//           "action": "Sell to Open"
	//       },
	//       {
	//           "instrument-type": "Equity Option",
	//           "symbol": "AAPL  221118C00157500",
	//           "quantity": 1,
	//           "action": "Buy to Open"
	//       }
	//     ]
	// }
];

function MeDocumentationExamples({ location, provider, onSubmit }) {

	// check example order conditions:
	// is currently selected provider handles orders with such params?
	const isAvailable = useCallback(c => Object.keys(c || {}).every(item =>
		(typeof c[item] == 'boolean') && (item in provider.reference) ||
		(typeof c[item] == 'string') && (provider.reference[item]?.values?.includes(c[item]))
	));

	useEffect(() => {
		if (!location.hash) { return; }

		scrollToHash(location.hash);

		const element = document.
			getElementById(location.hash.substring(1))?.
			querySelector('[data-bs-parent="#examples"]');

		element && Collapse.getOrCreateInstance(element).show();
	}, []);

	return (
		<>
			<div className="fw-bold">Examples</div>

			<div>
				The following are examples json bodies with explanations
				for some common use cases.
			</div>

			<style>{`
				.accordion-header .anchor-link { display: none }
				.accordion-header:hover .anchor-link { display: block }
			`}</style>

			<div className="accordion mt-3" id="examples">
				{
					EXAMPLES.map((item, index, arr) =>
						<div id={'examples-' + item.key} key={index} className="accordion-item">
							<h2 className="accordion-header">
								<button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={'#examples-' + item.key + '-collapse'}>
									{ item.title }
									&nbsp;<a className="anchor-link link-underline link-underline-opacity-100" aria-label="Link to this section" href={'#examples-' + item.key}>#</a>
								</button>
							</h2>
							<div id={'examples-' + item.key + '-collapse'} className="accordion-collapse collapse" data-bs-parent="#examples">

								<div className="accordion-body">
									{ item.description }
								</div>

								<div className="accordion-body position-relative bg-light text-dark">
									<button
										type="button"
										className="btn btn-light position-absolute"
										data-bs-toggle="reserved-tooltip"
										data-bs-title="Copy to clipboard"
										style={{top: '0.75em', right: '0.75em', zIndex: 11}}
										onClick={() => setClipboard(JSON.stringify(item.body, null, 2))}
									>
										<Icon.Copy />
									</button>

									<button
										type="button"
										className="btn btn-light position-absolute opacity-25"
										style={{bottom: '1.25em', right: '0.75em', zIndex: 10}}
										onClick={onSubmit.bind(this, item.body)}
										disabled
									>
										Submit&nbsp;<Icon.CaretRigth />
									</button>

									<pre className="mb-0">{ JSON.stringify(item.body, null, 2) }</pre>
								</div>

								{
									!isAvailable(item.conditions) &&
										<div className={'accordion-body position-relative bg-warning-subtle text-dark' + (arr.length == index + 1  && 'rounded-bottom')}>
											This type of order is not available for {provider.title}
										</div>
								}
							</div>
						</div>
					)
				}
			</div>
		</>
	);
}

export default withRouter(MeDocumentationExamples);
